import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['group', 'row', 'details', 'icon']

  static values = {
    storageKey: String
  }

  connect () {
    this.sortColumn = 'quoteNumber'
    this.sortDirection = 'desc'

    this.restoreState()
    this.updateSortIcons()
  }

  toggle (event) {
    event.stopPropagation()

    const quoteId =
      event.currentTarget.dataset.quoteId ||
      event.currentTarget.closest('[data-quote-id]')?.dataset.quoteId

    if (!quoteId) return

    const details = this.detailsTargets.find(
      element => element.dataset.quoteId === quoteId
    )

    const row = this.rowTargets.find(
      element => element.dataset.quoteId === quoteId
    )

    const icon = this.iconTargets.find(
      element => element.dataset.quoteId === quoteId
    )

    if (!details || !row || !icon) return

    const willOpen = details.classList.contains('hidden')

    details.classList.toggle('hidden', !willOpen)
    icon.classList.toggle('rotate-90', willOpen)
    row.setAttribute('aria-expanded', willOpen.toString())

    this.saveState()
  }

  sort (event) {
    const column = event.params.sort
    const type = event.params.type

    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      this.sortColumn = column
      this.sortDirection = 'asc'
    }

    const groups = [...this.groupTargets]

    groups.sort((left, right) => {
      const leftValue = this.sortValue(left.dataset[column], type)
      const rightValue = this.sortValue(right.dataset[column], type)

      const result = this.compareValues(leftValue, rightValue, type)

      return this.sortDirection === 'asc' ? result : -result
    })

    groups.forEach(group => {
      group.parentElement.appendChild(group)
    })

    this.updateSortIcons()
    this.saveState()
  }

  sortValue (value, type) {
    if (value === undefined || value === null || value === '') {
      return type === 'string' || type === 'natural' ? '' : 0
    }

    switch (type) {
      case 'number':
        return Number(value)
      case 'date':
        return Date.parse(value) || 0
      case 'natural':
        return value.toLowerCase()
      default:
        return value.toLowerCase()
    }
  }

  compareValues (leftValue, rightValue, type) {
    if (type === 'natural') {
      return leftValue.localeCompare(
        rightValue,
        undefined,
        {
          numeric: true,
          sensitivity: 'base'
        }
      )
    }

    if (leftValue < rightValue) return -1
    if (leftValue > rightValue) return 1

    return 0
  }

  expandedQuoteIds () {
    return this.detailsTargets
      .filter(element => !element.classList.contains('hidden'))
      .map(element => element.dataset.quoteId)
  }

  restoreState () {
    if (!this.hasStorageKeyValue) return

    try {
      const state = JSON.parse(
        sessionStorage.getItem(this.storageKeyValue)
      )

      if (!state) return

      this.sortColumn = state.sortColumn || this.sortColumn
      this.sortDirection = state.sortDirection || this.sortDirection

      const expandedQuoteIds = state.expandedQuoteIds || []

      expandedQuoteIds.forEach(quoteId => {
        const details = this.detailsTargets.find(
          element => element.dataset.quoteId === quoteId
        )

        const row = this.rowTargets.find(
          element => element.dataset.quoteId === quoteId
        )

        const icon = this.iconTargets.find(
          element => element.dataset.quoteId === quoteId
        )

        if (!details || !row || !icon) return

        details.classList.remove('hidden')
        icon.classList.add('rotate-90')
        row.setAttribute('aria-expanded', 'true')
      })

      this.applyStoredSort()
    } catch (error) {
      console.warn('Unable to restore quote table state.', error)
    }
  }

  applyStoredSort () {
    const groups = [...this.groupTargets]

    groups.sort((left, right) => {
      const type = this.columnType(this.sortColumn)
      const leftValue = this.sortValue(
        left.dataset[this.sortColumn],
        type
      )

      const rightValue = this.sortValue(
        right.dataset[this.sortColumn],
        type
      )

      const result = this.compareValues(leftValue, rightValue, type)

      return this.sortDirection === 'asc' ? result : -result
    })

    groups.forEach(group => {
      group.parentElement.appendChild(group)
    })
  }

  columnType (column) {
    switch (column) {
      case 'quoteNumber':
        return 'natural'
      case 'amount':
        return 'number'
      case 'created':
      case 'expires':
        return 'date'
      default:
        return 'string'
    }
  }

  updateSortIcons () {
    this.element
      .querySelectorAll('[data-quote-table-sort-icon]')
      .forEach(icon => {
        const column = icon.dataset.quoteTableSortIcon

        if (column !== this.sortColumn) {
          icon.textContent = '↕'
          return
        }

        icon.textContent =
          this.sortDirection === 'asc' ? '↑' : '↓'
      })
  }

  saveState () {
    if (!this.hasStorageKeyValue) return

    sessionStorage.setItem(
      this.storageKeyValue,
      JSON.stringify({
        expandedQuoteIds: this.expandedQuoteIds(),
        sortColumn: this.sortColumn,
        sortDirection: this.sortDirection
      })
    )
  }
}
